import { Component, DoCheck, Input } from '@angular/core';
import { ControlService } from '../library/control.service';
import { DeviceSettingsService } from '../library/device-settings.service';
import { PlayerService } from '../library/player.service';
import { Router } from '@angular/router';
import { IConverseStatus } from 'src/app/interfaces/iconverse-status';
import { environment } from 'src/environments/environment';
import { CallStoreService } from '../library/call-store/call-store.service';
import { AssemblyService } from '../library/assembly/assembly.service';
import { RealtimeService } from '../library/realtime/realtime.service';
import { LoggerService } from 'src/app/services/logger/logger.service';

@Component({
  selector: 'app-no-response-detected',
  templateUrl: './no-response-detected.component.html',
  styleUrls: ['./no-response-detected.component.scss'],
  standalone: false,
})
export class NoResponseDetectedComponent implements DoCheck {
  private previousStatus: IConverseStatus | null = null;

  @Input() controlService: ControlService | undefined;
  @Input() deviceSettingsService: DeviceSettingsService | undefined;
  @Input() playerService: PlayerService | undefined;
  @Input() realtimeService: RealtimeService | undefined;
  disabledButtons: boolean = false;
  waitingForResponse: boolean = false;
  timeout: any;
  component: any;

  constructor(
    private router: Router,
    public callStore: CallStoreService,
    private assemblyService: AssemblyService,
    private logger: LoggerService
  ) {}

  ngOnInit(): void {
    this.logger.log('[CALL] no response - modal', {
      modalMessage: 'No response detected modal displayed'
    });

    this.waitingForResponse = false;
    this.callStore.setIsResumeFromModal(true);
    this.callStore.setResumeFromModalType('no-response-detected');
    // validate if audio device is set
    if (this.deviceSettingsService) {
      const audioDeviceSelected =
        this.deviceSettingsService.getAudioDeviceLocalStorage();
      const isAudioDeviceSelected = audioDeviceSelected ? true : false;
      this.callStore.setIsAudioDeviceSelected(isAudioDeviceSelected);

      this.logger.log('[CALL] no response - detected init', {
        audioDeviceSelected: audioDeviceSelected,
      });
    }
  }

  ngOnDestroy(): void {
    clearTimeout(this.timeout);
  }

  checkSettings(): void {
    this.disabledButtons = true;
    if (!this.controlService) {
      console.error('Control service not found on NoResponseDetectedComponent');
      this.disabledButtons = false;
      return;
    }
    this.callStore.setOpenNoResponseModal(false);

    if (!this.deviceSettingsService) {
      this.logger.error(
        '[CALL] no response - detected display settings',
        'Device settings service not found'
      );

      this.disabledButtons = false;
      return;
    }

    this.logger.log('[CALL] no response - detected display settings');

    this.deviceSettingsService.displayDeviceSettingsModal();
    this.disabledButtons = false;
  }

  async tryAgain(): Promise<void> {
    this.disabledButtons = true;
    if (!this.controlService) {
      console.error('Control service not found on NoResponseDetectedComponent');
      this.disabledButtons = false;
      return;
    }
    this.callStore.setIsLoadingNoResponse(true);
    this.timeout = setTimeout(() => {
      this.waitingForResponse = true;
      console.log('More than 4 seconds have passed. Since click on Try Again');
    }, 5000);

    const isRealtime = this.controlService.isRealTimeCall;
    if (isRealtime) {
      this.realtimeService?.unpauseCall();
      this.assemblyService.validateWebAudioStatus();
      this.disabledButtons = false;

      this.logger.log('[CALL] no response - detected try again');
      return;
    }

    await this.controlService.unpauseCall();
    await this.assemblyService.validateWebAudioStatus();
    this.disabledButtons = false;

    this.logger.log('[CALL] no response - detected try again');
  }

  async closeModal(): Promise<void> {
    this.disabledButtons = true;
    if (!this.controlService) {
      console.error('Control service not found on NoResponseDetectedComponent');
      this.disabledButtons = false;
      return;
    }
    this.callStore.setIsLoadingNoResponse(false);
    const isRealtime = this.controlService.isRealTimeCall;
    if (isRealtime) {
      this.realtimeService?.unpauseCall();
    } else {
      await this.controlService.unpauseCall();
    }
    this.callStore.setOpenNoResponseModal(false);
    this.disabledButtons = false;

    this.logger.log('[CALL] no response - detected close modal');
  }

  goAssignments() {
    this.router.navigate([`/assignments`]);

    this.logger.log('[CALL] no response - detected go assignments');
  }

  waitForResponse(): void {
    if (this.playerService) {
      this.playerService.converseStatus = 'retrying';
      if (this.playerService.conversationStatusCode === 0) {
        this.playerService.answerSentDateTime = new Date();
        this.playerService.startConversationalInterval();
      } else {
        this.playerService.sendAnswerConversational(true, false);
      }
    }
    this.callStore.setIsLoadingNoResponse(true);

    this.logger.log('[CALL] no response - detected wait for response', {
      function: 'waitForResponse',
    });
  }

  continueAvatarReady(): void {
    if (this.playerService) {
      this.playerService.converseStatus = null;
      this.playerService.answerSentDateTime = null;
      this.callStore.setIsLoadingNoResponse(false);
      this.callStore.setOpenNoResponseModal(false);
      const resp = this.playerService.shouldHandleResponse;
      if (resp) {
        this.playerService.handleConversationalResponse(resp);
      }
    }

    this.logger.log('[CALL] no response - detected continue avatar ready');
  }

  ngDoCheck() {
    if (this.playerService) {
      if (this.playerService.converseStatus !== this.previousStatus) {
        this.previousStatus = this.playerService.converseStatus;
        console.log(
          'NO-RESPONSE_DETECTED converseStatus changed to:',
          this.previousStatus
        );
        this.logSentry();
      }
    }
  }

  logSentry() {
    const isLoadingNoResponse = this.callStore.getIsLoadingNoResponse();
    const { converseStatus } = this.playerService || {};
    const isInternetConnected = navigator.onLine;

    const userUuid = localStorage.getItem('user_uuid');
    const sessionUuid = this.callStore.getCall()?.practiceCallUuid || '';
    const callName = this.callStore.getCall()?.callName || '';

    const sentryTagsPayload = {
      user_uuid: userUuid ?? '',
      session_uuid: sessionUuid,
      call_name: callName,
    };

    let state = 0;
    if (isLoadingNoResponse) state = 1;
    if (
      converseStatus === 'thinking' &&
      isInternetConnected &&
      !isLoadingNoResponse
    )
      state = 2;
    if (
      converseStatus === 'unavailable' &&
      isInternetConnected &&
      !isLoadingNoResponse
    )
      state = 3;

    const messages: Record<number, string> = {
      1: 'Loading...',
      2: 'Avatar needs more time to think',
      3: "Avatar is still thinking; we'll let you know when they're ready or return to Assignments",
    };

    console.log('SENTRY LOG:', state);
    console.log('SENTRY LOG Payload:', sentryTagsPayload);

    if (messages[state] && environment.production) {
      console.log('LOGING TO SENTRY');
      this.logger.error('[CALL] no response detected', new Error(messages[state]), undefined, sentryTagsPayload);
    }

    this.logger.log('[CALL] no response - detected log sentry', {
      log: messages[state] || 'No message',
    });
  }

  protected get isInternetConnected() {
    return navigator.onLine;
  }
}
